import React, { useCallback, useContext, useState, useMemo } from 'react';
import {
  Lead,
  LeadStagesCopy,
  LeadStages,
  LeadState,
  LEAD_STAGES,
} from 'models/Lead';
import { TABLE_COLUMNS } from '../../data';
import usePastTime from 'hooks/usePastTime';
import classNames from 'classnames';
import { Button, Dropdown, Loader } from 'ncoded-component-library';
import ResolvePopup from 'components/ResolvePopup';
import { useTranslation } from 'react-i18next';
import utils, {
  formatWithLimit,
  getDataRoomRoute,
  getEmailDomain,
  getUserFullName,
  isNdaNotSent,
  sortReleasedFiles,
} from 'utils';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import ChatIcon from 'icons/Chat.icon';
import NdaStatusBadge from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/components/NdaSigningProgress/NdaStatusBadge';
import LinkToIcon from 'icons/LinkTo.icon';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import LeadsContext from '../../../../providers/Leads/Leads.context';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { LEAD_DRAWER_OPTIONS } from '../../../../../../models';
import DocumentWithPencilIcon from 'icons/DocumentWithPencil.icon';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import TrashIcon from 'icons/Trash.icon';
import confirm from 'modules/confirm';
import { SUCCESS_MESSAGES } from 'request-responses';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { NEW_WINDOW_PROPS } from 'data/global';
import NewBadge from '../../../NewBadge';
import SendNda from '../SendNda';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import { SIZE } from 'models/Size';
import ReleaseFiles from '../../../ReleaseFiles';
import DealContext from '../../../../providers/Deal/Deal.context';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import useLead from '../../../../hooks/useLead';
import { formatPhoneNumber } from 'phoneNumberUtils';

import './LeadsTableCell.styles.scss';

const STATE_INDEX = 0;
const MAX_NUM_CHARS = 70;

type CellProps = {
  lead: Lead;
  updateLead?: (lead: Lead) => void;
  removeLead?: (leadId: string) => void;
};

const NameCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { firstName, lastName } = lead?.buyer || {};

  return (
    <div className="lds-name-cell">{getUserFullName(firstName, lastName)}</div>
  );
};

const STATES_TO_STAGES: Record<LeadState, LeadStages> = {
  enquire: LEAD_STAGES.ENQUIRE,
  enquireWaitlisted: LEAD_STAGES.ENQUIRE,
  accept: LEAD_STAGES.ACCEPT,
  acceptWaitlisted: LEAD_STAGES.ACCEPT,
  confirm: LEAD_STAGES.CONFIRM,
  confirmWaitlisted: LEAD_STAGES.CONFIRM,
  offer: LEAD_STAGES.OFFER,
  offerWaitlisted: LEAD_STAGES.OFFER,
};

const StageCellComponent: React.FC<CellProps> = ({ lead }) => {
  const {
    firstName,
    lastName,
    email,
    leadState,
    withdrawnInterest,
    isPrivateViewer,
    isRequestedPrivateViewer,
    isInactive,
  } = lead;

  const stage = STATES_TO_STAGES[leadState];
  const leadName = getUserFullName(firstName, lastName) || email;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();
  const [isLoading, setIsLoading] = useState(false);

  const { popSuccess } = useContext(PopNotificationsContext);
  const { changeState, openWithdrawModal } = useContext(LeadsContext);
  const { canManageDeal } = useContext(ListingContext);
  const { leadStagesCopies } = useContext(DealContext);

  const { t } = useTranslation();

  const stageTrigger = useMemo(
    () => (
      <Button
        className={classNames({
          withdrawn: withdrawnInterest,
        })}
      >
        {t(
          isInactive
            ? 'inactive'
            : isPrivateViewer
            ? 'invited'
            : isRequestedPrivateViewer
            ? 'interested'
            : withdrawnInterest
            ? 'withdrawn'
            : leadStagesCopies?.[stage],
        )}
        {canManageDeal && <ChevrondownIcon />}
      </Button>
    ),
    [
      canManageDeal,
      isPrivateViewer,
      isRequestedPrivateViewer,
      leadStagesCopies,
      stage,
      withdrawnInterest,
      isInactive,
      t,
    ],
  );

  const onStateChange = useCallback(
    async (state: LeadStages) => {
      dropdown?.setIsOpen(false);

      if (
        await confirm(
          {
            title: t('moveLeadToNewState', {
              lead: leadName,
              state: leadStagesCopies?.[state],
            }),
            content: t('moveLeadToNewStateDescription'),
            type: 'delete',
            submitBtnText: t('confirm'),
            cancelBtnText: t('cancel'),
          },
          true,
        )
      ) {
        try {
          setIsLoading(true);
          await changeState(lead, state, STATE_INDEX);
          popSuccess(t(SUCCESS_MESSAGES.CHANGE_LEAD_STATE));
        } catch {
          return;
        } finally {
          setIsLoading(false);
        }
      }
    },
    [dropdown, leadName, lead, leadStagesCopies, changeState, popSuccess, t],
  );

  if (!canManageDeal)
    return <div className="lds-stage-cell">{stageTrigger}</div>;

  return (
    <div className="lds-stage-cell">
      <Dropdown
        renderAsPortal
        ref={dropdownRef}
        disabled={isLoading}
        trigger={stageTrigger}
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="stage-cell__actions">
          {(Object.entries(leadStagesCopies) as [LeadStages, LeadStagesCopy][])
            .filter(
              ([key]) =>
                isPrivateViewer ||
                isRequestedPrivateViewer ||
                withdrawnInterest ||
                key !== stage,
            )
            .map(([key, value]) => (
              <li
                key={key}
                className={STATES_TO_STAGES[key]}
                onClick={() => onStateChange(key)}
              >
                {t(value)}
              </li>
            ))}

          {!withdrawnInterest && !isPrivateViewer && !isRequestedPrivateViewer && (
            <li className="withdrawn" onClick={() => openWithdrawModal(lead)}>
              {t('withdrawn')}
            </li>
          )}
        </ul>
      </Dropdown>
    </div>
  );
};

export const NdaCellComponent: React.FC<CellProps> = ({ lead, updateLead }) => {
  const {
    signingStatus,
    envelopeTemplateId,
    sentDate,
    signedDate,
    withdrawnInterest,
    rejectionReason,
  } = lead;

  const { canManageDeal } = useContext(ListingContext);

  const isNew = useLead().getIsNew(lead);

  const { t } = useTranslation();

  if (isNew) return <NewBadge />;

  if (!isNdaNotSent(signingStatus))
    return (
      <div className="lds-nda-cell">
        <NdaStatusBadge
          status={signingStatus}
          sentDate={sentDate}
          signedDate={signedDate}
          rejectionReason={rejectionReason}
        />
      </div>
    );

  if (!envelopeTemplateId || withdrawnInterest)
    return <span className="lds-nda-cell no-nda">{t('noNda')}</span>;

  if (canManageDeal) return <SendNda lead={lead} updateLead={updateLead} />;

  return null;
};

export const DocumentsCellComponent: React.FC<CellProps> = ({ lead }) => {
  const {
    _id,
    dataRoomTopNodes: dataRoomNodeIdWithStateAndSignals,
    canManageDataRoom,
  } = lead || {};

  const { canAccessDataRoom } = useContext(ListingContext);
  const {
    dealRoom: { _id: dealRoomId, dataRoomId, numberOfFiles },
    dealRoomTopNodes,
  } = useContext(DealContext);
  const { updateLead } = useContext(LeadsContext);

  const { t } = useTranslation();

  const sortedDataRoomNodesWithStateAndSignal = useMemo(() => {
    const data = dealRoomTopNodes.map(({ dataRoomNodeId, dataRoomNode }) => {
      const found = dataRoomNodeIdWithStateAndSignals.find(
        (node) => node.dataRoomNodeId === dataRoomNodeId,
      );

      return { ...found, dataRoomNode };
    });

    return sortReleasedFiles(data);
  }, [dealRoomTopNodes, dataRoomNodeIdWithStateAndSignals]);

  const noFilesInDataRoom = !numberOfFiles;

  const openDataRoom = useCallback(
    (e) => {
      e.stopPropagation();
      window.open(
        getDataRoomRoute(dataRoomId, 'documents'),
        NEW_WINDOW_PROPS.target,
      );
    },
    [dataRoomId],
  );

  if (!canAccessDataRoom) return null;

  return (
    <div className="lds-documents-cell">
      {noFilesInDataRoom ? (
        <Button
          className="no-dr"
          iconPosition="right"
          icon={LinkToIcon}
          onClick={openDataRoom}
        >
          {t('populateDR')}
        </Button>
      ) : (
        <ReleaseFiles
          leadId={_id}
          dealRoomId={dealRoomId}
          disabled={!canManageDataRoom}
          dataRoomNodesWithStateAndSignal={
            sortedDataRoomNodesWithStateAndSignal
          }
          onSuccessRelease={updateLead}
        />
      )}
    </div>
  );
};

export const ChatCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { openLeadDrawer } = useContext(LeadsContext);
  const { areActionsDisabled } = useContext(ListingContext);

  const { sessionId, unreadMessagesCount, unrepliedMessagesCount } = lead;

  const { t } = useTranslation();

  if (!sessionId) return null;

  const count = unreadMessagesCount || unrepliedMessagesCount;

  return (
    <div className="lds-message-cell">
      <Button
        disabled={areActionsDisabled}
        {...(!areActionsDisabled && {
          onClick: (e) => {
            e.stopPropagation();
            openLeadDrawer(lead, LEAD_DRAWER_OPTIONS.MESSAGES);
          },
        })}
      >
        {count ? (
          <ResolvePopup
            onHover
            popupContent={t(
              unreadMessagesCount
                ? 'unreadMessagesWaiting'
                : 'messageAwaitingYourReply',
            )}
          >
            <ChatIcon />
            <Counter
              count={count}
              isPositionAbsolute
              size={SIZE.SMALL}
              variant={
                unreadMessagesCount
                  ? COUNTER_VARIANTS.ORANGE
                  : COUNTER_VARIANTS.SILVER
              }
            >
              {formatWithLimit(count)}
            </Counter>
          </ResolvePopup>
        ) : (
          <ChatIcon />
        )}
      </Button>
    </div>
  );
};

const EmailCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { email } = lead || {};

  const copyToClipboard = useCopyToClipboard();

  if (!email) return null;

  return (
    <Button
      className="lds-email-cell"
      variant="link"
      onClick={(e) => {
        copyToClipboard(e, email);
      }}
    >
      <p className="info">{getEmailDomain(email)}</p>
      <ClipboardcopyIcon />
    </Button>
  );
};

const PhoneCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { contactNumber, buyer } = lead || {};

  const copyToClipboard = useCopyToClipboard();

  const formattedPhone = formatPhoneNumber(contactNumber || buyer?.phone);

  if (!formattedPhone) return null;

  return (
    <Button
      className="lds-phone-cell"
      variant="link"
      onClick={(e) => {
        copyToClipboard(e, formattedPhone);
      }}
    >
      <p className="info">{formattedPhone}</p>
      <ClipboardcopyIcon />
    </Button>
  );
};

const CompanyCellComponent: React.FC<CellProps> = ({ lead }) => {
  const companyName = lead?.buyer?.companyName;

  return <div className="lds-company-cell">{companyName}</div>;
};

const LastSeenCellComponent: React.FC<CellProps> = ({ lead }) => {
  const lastSeen = lead?.lastSeen;

  const pastTime = usePastTime()(lastSeen);

  if (!pastTime) return null;

  return <div className="lds-last-seen-cell">{pastTime}</div>;
};

const NotesCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { openLeadDrawer } = useContext(LeadsContext);

  return (
    <div className="lds-notes-cell">
      <Button
        icon={DocumentWithPencilIcon}
        onClick={(e) => {
          e.stopPropagation();
          openLeadDrawer(lead, LEAD_DRAWER_OPTIONS.NOTES);
        }}
      >
        <Counter
          count={lead?.unreadNotesCount || lead?.unrepliedNotesCount}
          isPositionAbsolute
          size={SIZE.SMALL}
          variant={
            lead?.unreadNotesCount
              ? COUNTER_VARIANTS.ORANGE
              : COUNTER_VARIANTS.SILVER
          }
        />
      </Button>
    </div>
  );
};

const AddedCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { createdAt } = lead;

  const pastTime = usePastTime()(createdAt);

  if (!pastTime) return null;

  return <div className="lds-add-cell">{pastTime}</div>;
};

const ReferrerCellComponent: React.FC<CellProps> = ({ lead }) => {
  return <div className="lds-referrer-cell">{lead.referrerName}</div>;
};

const DeleteCellComponent: React.FC<CellProps> = ({ lead, removeLead }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { removeLead: removeLeadCard } = useContext(LeadsContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const onDelete = useCallback(
    async (e) => {
      e.stopPropagation();

      if (
        await confirm(
          {
            title: t('areYouSureDeleteLead'),
            content: t('deleteWarning'),
            type: 'delete',
            submitBtnText: t('deleteLeadConfirm'),
            cancelBtnText: t('deleteLeadReject'),
          },
          true,
        )
      ) {
        try {
          setIsLoading(true);
          await api.dealRoom.deleteLead(lead?._id);
          removeLead(lead?._id);
          removeLeadCard(lead?._id);
          popSuccess(t(SUCCESS_MESSAGES.DELETED_LEAD));
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [lead?._id, popServerError, popSuccess, removeLead, removeLeadCard, t],
  );

  if (isLoading) {
    return (
      <div className="lds-delete-cell">
        <Loader />
      </div>
    );
  }

  return (
    <div className="lds-delete-cell">
      <Button
        className="warning-action-button"
        onClick={onDelete}
        icon={TrashIcon}
      />
    </div>
  );
};

export const ContextCellComponent: React.FC<CellProps> = ({ lead }) => {
  const { context } = lead;

  const hasMoreChars = context?.length > MAX_NUM_CHARS;

  const { t } = useTranslation();

  const shortenedString = utils.getShortenedString(context, MAX_NUM_CHARS);

  if (!context)
    return <div className="lds-context-cell">{t('noContextForLead')}</div>;

  if (hasMoreChars)
    return (
      <ResolvePopup onHover className="lds-context-cell" popupContent={context}>
        {shortenedString}
      </ResolvePopup>
    );

  return <div className="lds-context-cell">{context}</div>;
};

export const BODY_CONTENT_GENERATE: Record<
  typeof TABLE_COLUMNS[number],
  (cellProps: CellProps) => React.ReactNode
> = {
  name: (cellProps: CellProps) => <NameCellComponent {...cellProps} />,
  stage: (cellProps: CellProps) => <StageCellComponent {...cellProps} />,
  nda: (cellProps: CellProps) => <NdaCellComponent {...cellProps} />,
  fileAccess: (cellProps: CellProps) => (
    <DocumentsCellComponent {...cellProps} />
  ),
  chat: (cellProps: CellProps) => <ChatCellComponent {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCellComponent {...cellProps} />,
  phone: (cellProps: CellProps) => <PhoneCellComponent {...cellProps} />,
  lastSeen: (cellProps: CellProps) => <LastSeenCellComponent {...cellProps} />,
  company: (cellProps: CellProps) => <CompanyCellComponent {...cellProps} />,
  added: (cellProps: CellProps) => <AddedCellComponent {...cellProps} />,
  notes: (cellProps: CellProps) => <NotesCellComponent {...cellProps} />,
  referrer: (cellProps: CellProps) => <ReferrerCellComponent {...cellProps} />,
  delete: (cellProps: CellProps) => <DeleteCellComponent {...cellProps} />,
  context: (cellProps: CellProps) => <ContextCellComponent {...cellProps} />,
};
